import React, { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import mergeRight from "ramda/src/mergeRight";
import append from "ramda/src/append";
import compose from "ramda/src/compose";
import pathOr from "ramda/src/pathOr";
import Api from "../../services/Api";
import { Modal } from "../Modal";
import { Button } from "../../UI/Button";
import { useModalContext } from "../../contexts/ModalContext/context";
import { Grid } from "../../UI/Grid";
import { EMAIL_ADDRESS, PHONE_NUMBER } from "../../constants/general";
import { useNetwork } from "../../hooks/useNetwork";
import useAuth from "../../routes/login/AuthContext";
import { getControllerCheckboxRender } from "../../helpers/getControllerCheckboxRender";
import { Typography } from "../../UI/Typography";
import { Link } from "../Link";
import { COLOR_STALE_GRAY } from "../../constants/styles";
import { Checkbox } from "../../UI/Checkbox";
import { useSnackbar } from "../../hooks/useSnackbar";
import { L3 } from "../../services/NetworkApi";

export interface IProps {
  visible: boolean;
  onHide: any;
  l1Id: number;
  l2Id: number;
  l3Id?: number;
}

export const ApplyToOthersModal: React.FC<IProps> = ({ visible, onHide, l1Id, l2Id, l3Id }) => {
  const [accepted, setAccepted] = useState(false);
  const [isLoading, setLoading] = useState(false);
  const { getL2Options, getL2 } = useNetwork();
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar();
  const { getAuthToken } = useAuth();
  const { setIsOpen } = useModalContext();
  const {
    formState: { errors },
    control,
    handleSubmit,
  } = useForm();

  useEffect(() => {
    setAccepted(false);
    setIsOpen(visible);
  }, [visible]);

  const currentSubnetwork = getL2(l1Id, l2Id);
  const subnetworks = getL2Options(l1Id).filter(
    (subnetwork: any) => subnetwork.matchingRoundStatus !== "ARCHIVED");


  if (!subnetworks || !currentSubnetwork) {
    return null;
  }

  const currentUnit = currentSubnetwork.l3.find((l3) => l3.l3Id === l3Id);

  const getDefaultInputProps = mergeRight({ errors });

  const getCheckboxControllerRender = compose<any, any, any>(getControllerCheckboxRender, getDefaultInputProps);

  const handleUpdate = async (data: any) => {
    setLoading(true);
    const targets = Object.keys(data).reduce((acc: any, key: string) => {
      if (!data[key]) {
        return acc;
      }

      const [l2Id, l3Id] = key.split("-");

      return append(
        {
          l1Id,
          l2Id: Number(l2Id),
          l3Id: Number(l3Id),
        },
        acc
      );
    }, []);

    const response = await Api.requestCopyQuestions(l1Id, l2Id, l3Id, targets, await getAuthToken());

    if (!response.error) {
      showSuccessSnackbar("Successfully copied");
      onHide();
    } else {
      showErrorSnackbar(pathOr("An error occurred. Please try again later!", ["error", "description"], response));
    }
    setLoading(false);
  };

  return (
    <Modal
      maxWidth="md"
      onClose={onHide}
      title="Apply Lifestyle Questions to Other Units"
      actions={
        <>
          <Button appearance="subtle" onClick={() => onHide()}>
            Cancel
          </Button>
          <Button
            appearance="primary"
            onClick={handleSubmit(handleUpdate)}
            isLoading={isLoading}
            isDisabled={!accepted}
          >
            Update Lifestyle Questions
          </Button>
        </>
      }
    >
      <Grid container spacing={2} px={3} width={706}>
        <Grid item xs={12}>
          <Typography variant="body2" gutterBottom>
            The current lifestyle questions options for{" "}
            {
              <b>
                {currentSubnetwork.l2Name} / {currentUnit?.name}
              </b>
            }{" "}
            will replace the lifestyle questions options for the unit types selected below. Please select the unit types
            to overwrite for lifestyle questions options:
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {subnetworks.map((subnetwork: any) => (
              <Grid item xs={12} key={subnetwork.l2Id}>
                <Typography
                  mb={1}
                  fontSize={12}
                  fontWeight={700}
                  color={COLOR_STALE_GRAY}
                  sx={{ textTransform: "uppercase" }}
                >
                  {subnetwork.l2Name}
                </Typography>
                <Grid container>
                  {subnetwork.l3.map((l3: L3) => {
                    if (subnetwork.l2Id === currentSubnetwork.l2Id && l3.l3Id === currentUnit?.l3Id) {
                      return;
                    }

                    return (
                      <Grid item xs={12} key={l3.l3Id}>
                        <Controller
                          control={control}
                          name={String(`${subnetwork.l2Id}-${l3.l3Id}`)}
                          render={getCheckboxControllerRender({ label: l3.name })}
                        />
                      </Grid>
                    );
                  })}
                </Grid>
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Typography variant="body2" gutterBottom>
            If you have questions about how clears work, please call us at {PHONE_NUMBER} or e-mail{" "}
            <Link href={`mailto:${EMAIL_ADDRESS}`}>{EMAIL_ADDRESS}</Link>
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Checkbox
            name="accept"
            isChecked={accepted}
            onChange={() => setAccepted(!accepted)}
            label="I understand that overwritten lifestyle questions cannot be reinstated"
          />
        </Grid>
      </Grid>
    </Modal>
  );
};
